/* eslint-disable import/no-unresolved */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { isIOS } from 'react-device-detect';
import { OnboardingBodyLayout, SelectCountryDropdown } from 'assets/components';
import { PARTIAL_SIGN_UP_PHASES, RECAPTCHA_EVENTS, US_COUNTRY_ALPHA } from '../constants';
import { useHistoryState } from '../../../utilities/sharedHooks/history';
import { clearFormError, handleFormBlur } from '../../../utilities/formsUtils';
import {
  ADDRESS_STREET_US_MIN_LENGTH,
  validateAddressChange,
  validateInputLength,
} from '../../../utilities/formValidation';
import SelectUSStateDropdown from '../../../components/SelectUSStateDropdown';
import SelectStateRegionDropdown from '../../../components/SelectStateRegionDropdown';
import { settings } from '../../../api';
import {
  SETTINGS_COUNTRY_SUBDIVISIONS,
  SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY,
} from '../../../utilities/sharedQueries';
import { shouldAskStateRegion } from '../../../utilities/custodyProvider';
import { showAlert } from '../../../utilities/notifications';
import { USER_SIGNUP, PARTIAL_SIGN_UP } from '../mutations';
import {
  MXP_ONBOARDING_SIGN_UP_PHYSICAL_ADDRESS_VISIT,
  MXP_ONBOARDING_SIGN_UP_PHYSICAL_ADDRESS_CONFIRM,
} from '../mixpanel-events';
import {
  cacheRecaptchaV3failure,
  cacheUserFirstAccess,
  clearRecaptchaV3failure,
} from '../../../utilities/caching';
import { FLOWS, aliasUser, identifyUser, sendStats } from '../../../services/mixpanel';
import countriesPostcodes from '../../../utilities/countriesPostcodes';
import Input from '../../../components/Input';
import { OnboardingContext } from '../../../contexts/onboarding/context';
import { TrackGoogleAnalyticsEvent } from '../../../utilities/google-analytics';
import RecaptchaModal from '../../shared/RecaptchaModal';

const AddPhysicalAddress = () => {
  const { t: translate } = useTranslation();

  const history = useHistory();

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isOpenRecV2, setIsOpenRecV2] = useState(false);
  const [recV2Token, setRecV2Token] = useState('');

  const { state } = useLocation();
  const isUS = state?.countryOfResidenceAlpha === US_COUNTRY_ALPHA;

  useEffect(() => {
    sendStats(MXP_ONBOARDING_SIGN_UP_PHYSICAL_ADDRESS_VISIT, {}, FLOWS.ONBOARDING);
  }, []);

  const selectDropDownFields = [
    {
      type: 'img',
      data: 'iso3166alpha2',
      class: 'col-1 text-left position-relative',
      img: {
        sourcePath: './images/flags/1x1/',
        ext: '.svg',
        class: 'rounded-circle position-absolute top-50 start-0 translate-middle-y',
      },
    },
    {
      type: 'text',
      data: 'displayName',
      class: 'col-12 ps-3 text-left',
    },
  ];

  const stateDropdownFields = [
    {
      type: 'text',
      data: 'postal_abbreviation',
      class: 'col-11 ps-2 text-left',
    },
  ];

  const stateRegionDropdownFields = [
    {
      type: 'text',
      data: 'name',
      class: 'col-11 ps-2 text-left',
    },
  ];

  const [form, setForm] = useHistoryState('physicalAddressForm', {
    addressLine2: '',
    addressLine1: '',
    cityOrTown: '',
    postcode: '',
    selected_country_alpha: state?.countryOfResidenceAlpha || '',
    selected_country_label: state?.countryOfResidenceLabel || '',
    selected_country_alpha2: state?.countryOfResidenceAlpha2 || '',
    countryName: '',
    us_state: '',
    state_region: '',
    mailingAddress: true,
  });

  const { data: supportedCountries } = useQuery(SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY);
  const countryOfResidenceAlpha2 = state?.countryOfResidenceAlpha2;
  const isStateRegionCountry = useMemo(
    () => shouldAskStateRegion(supportedCountries, state?.countryOfResidenceAlpha, isUS),
    [supportedCountries, state?.countryOfResidenceAlpha, isUS]
  );
  const { data: subdivisionsData, isLoading: isLoadingSubdivisions } = useQuery(
    [SETTINGS_COUNTRY_SUBDIVISIONS, countryOfResidenceAlpha2],
    () => settings.countrySubdivisions(countryOfResidenceAlpha2),
    { enabled: isStateRegionCountry && Boolean(countryOfResidenceAlpha2) }
  );
  const subdivisions = useMemo(() => subdivisionsData?.subdivisions || [], [subdivisionsData]);
  const shouldShowStateRegion = isStateRegionCountry && subdivisions.length > 0;
  const isStateRegionMissing =
    isStateRegionCountry &&
    (isLoadingSubdivisions || subdivisions.length > 0) &&
    form.state_region === '';
  const stateRegion = shouldShowStateRegion ? form.state_region : undefined;

  const { state: onboardingState } = useContext(OnboardingContext);
  const allowPartialOnboarding = onboardingState?.mainSettings?.feature_enable_partial_registration;
  const allowPostcodeValidation =
    onboardingState?.mainSettings?.feature_toggle_enable_postcode_verification_signup;

  const { mutate: submitUserData, isLoading: isLoadingSubmitForm } = useMutation(USER_SIGNUP, {
    onSettled: (data, error) => {
      sendStats(MXP_ONBOARDING_SIGN_UP_PHYSICAL_ADDRESS_CONFIRM, {}, FLOWS.ONBOARDING);
      TrackGoogleAnalyticsEvent('Onboarding', 'sign_up', data);
      if (!error) {
        setIsOpenRecV2(false);
        clearRecaptchaV3failure();
        cacheUserFirstAccess();
        history.push(`/kyc`, {
          ...data,
          email: state?.email || state?.username,
          signupFlow: true,
        });
        aliasUser(data);
        identifyUser(data);
      } else if (error?.response?.status === 307) {
        setIsOpenRecV2(true);
        cacheRecaptchaV3failure();
      } else {
        setIsOpenRecV2(false);
        clearRecaptchaV3failure();
        showAlert({ message: error.message, type: 'danger' });
      }
    },
  });

  const { mutate: partialSignup, isLoading: isLoadingPartialSignup } = useMutation(
    PARTIAL_SIGN_UP,
    {
      onSettled: (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else if (isUS && !form?.mailingAddress) {
          sendStats(MXP_ONBOARDING_SIGN_UP_PHYSICAL_ADDRESS_CONFIRM, {}, FLOWS.ONBOARDING);
          history.push(`mailing-address`, {
            ...state,
            ...form,
            partialSignupData: data,
          });
        } else {
          sendStats(MXP_ONBOARDING_SIGN_UP_PHYSICAL_ADDRESS_CONFIRM, {}, FLOWS.ONBOARDING);
          cacheUserFirstAccess();
          aliasUser(data);
          history.push(`/kyc`, {
            ...data,
            email: state?.email || state?.username,
            signupFlow: true,
          });
        }
      },
    }
  );

  const validPostcode = useMemo(() => {
    if (allowPostcodeValidation) {
      const countryPostcode = countriesPostcodes.find(
        (country) => country.iso3166alpha2 === form?.selected_country_alpha2
      );
      if (countryPostcode) {
        const regex = new RegExp(countryPostcode.postal);
        const checkPostCode = regex.test(form?.postcode);

        return checkPostCode;
      }
    }
    return true;
  }, [allowPostcodeValidation, form?.postcode, form?.selected_country_alpha2]);

  const disabledButton = useMemo(
    () =>
      form.addressLine1 === '' ||
      (!isUS && !validateInputLength(form?.addressLine1, 24, false)) ||
      form.cityOrTown === '' ||
      !validateInputLength(form?.cityOrTown, 24, false) ||
      (!isUS && !!form?.addressLine2 && !validateInputLength(form?.addressLine2, 24)) ||
      form.postcode === '' ||
      !validPostcode ||
      (isUS && form.us_state === '') ||
      isStateRegionMissing,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, isUS, validPostcode, isStateRegionMissing]
  );

  useEffect(() => {
    if (!state?.email && !state?.partialSignupData?.email) {
      history.push('/onboarding/signup');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e, fieldName) => {
    if (!e) return;
    if (!e.target) {
      if (fieldName === 'selected_country') {
        setForm({
          ...form,
          selected_country_alpha: e.iso3166alpha3,
          selected_country_label: e.displayName,
        });
      } else if (fieldName === 'postcode') {
        setForm({ ...form, postcode: e?.toUpperCase() });
      } else {
        setForm({ ...form, [fieldName]: e });
      }
    } else {
      const { name, value } = e.target;
      if (name === 'mailingAddress') {
        setForm({ ...form, [name]: e.target.checked });
      } else if (name === 'postcode') {
        setForm({ ...form, [name]: value?.toUpperCase() });
      } else if (value) {
        const validAddress = validateAddressChange(
          value,
          isUS,
          name === 'addressLine1' ? 'street' : 'houseNumber',
          // just need to be true for US case DV-5733
          isUS
        );
        if (validAddress) setForm({ ...form, [name]: value });
      } else {
        setForm({ ...form, [name]: '' });
      }
    }
  };

  const handleBlur = (e) => {
    if (!e || !e.target) return;
    const { name, value, validity } = e?.target;
    if (name === 'cityOrTown') {
      const validLength = validateInputLength(value, 24, false);
      if ((!validLength && value) || (!validity.valid && value)) {
        e.target.className += ' has-error';
      }
    } else if (name === 'addressLine1') {
      const validLength = validateInputLength(form?.addressLine1, 24, false);
      if ((!validLength && value) || (!validity.valid && value)) {
        e.target.className += ' has-error';
      }
    } else if (name === 'addressLine2') {
      const validLength = validateInputLength(form?.addressLine2, 24, false);
      if ((!validLength && value) || (!validity.valid && value)) {
        e.target.className += ' has-error';
      }
    } else if (name === 'postcode') {
      if ((!validPostcode && value) || (!validity.valid && value) || !value) {
        e.target.className += ' has-error';
      }
    }
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    const validStreetAddress = validateAddressChange(form.addressLine1, isUS, 'street', false);
    if (isUS && !validStreetAddress) {
      if (form.addressLine1.length < ADDRESS_STREET_US_MIN_LENGTH) {
        showAlert({
          message: translate('error_address1_is_too_short_message_us'),
          type: 'danger',
        });
      } else {
        showAlert({
          message: translate('error_address1_contains_post_office_words_message_us'),
          type: 'danger',
        });
      }

      return;
    }

    if (!validPostcode) {
      showAlert({
        message: translate('postcode_error_message'),
        type: 'danger',
      });
      return;
    }

    try {
      if (!isUS || form.mailingAddress) {
        if (allowPartialOnboarding) {
          partialSignup({
            id: state?.partialSignupData?.id,
            phase: PARTIAL_SIGN_UP_PHASES.ADDRESS,
            address_line_1: form?.addressLine1,
            address_line_2: form?.addressLine2,
            postcode: form?.postcode,
            city_or_town: form?.cityOrTown,
            country_name: form?.selected_country_label,
            mailing_address: null,
            selected_country: form?.selected_country_alpha,
            us_state: form?.us_state,
            state_region: stateRegion,
            verification_id: state?.verification_id,
          });
        } else {
          const token = await executeRecaptcha(RECAPTCHA_EVENTS.SIGNUP_VERIFY_EMAIL);
          if (!token) {
            // eslint-disable-next-line consistent-return
            return false;
          }
          submitUserData({
            ...state,
            ...form,
            state_region: stateRegion,
            mailingAddress: null,
            token,
          });
        }
      } else if (allowPartialOnboarding) {
        partialSignup({
          id: state?.partialSignupData?.id,
          phase: PARTIAL_SIGN_UP_PHASES.ADDRESS,
          address_line_1: form?.addressLine1,
          address_line_2: form?.addressLine2,
          postcode: form?.postcode,
          city_or_town: form?.cityOrTown,
          country_name: form?.selected_country_label,
          selected_country: form?.selected_country_alpha,
          us_state: form?.us_state,
          state_region: stateRegion,
          verification_id: state?.verification_id,
        });
      } else {
        sendStats(MXP_ONBOARDING_SIGN_UP_PHYSICAL_ADDRESS_CONFIRM, {}, FLOWS.ONBOARDING);
        history.push(`mailing-address`, {
          ...state,
          ...form,
        });
      }
    } catch (error) {
      // eslint-disable-next-line consistent-return
      return false;
    }
  };

  const renderButtons = () => (
    <>
      <div className="d-none d-md-block w-100">
        <button
          className="btn btn-primary w-100 text-bold"
          type="submit"
          form="physical-address-form"
          loading={isLoadingSubmitForm || isLoadingPartialSignup ? 'true' : 'false'}
          disabled={disabledButton}>
          {translate('confirm_button_label')}
        </button>
      </div>
      <div className="d-block d-md-none position-fixed bottom-0 bg-lighter w-100 py-3 px-3">
        <button
          className="btn btn-primary w-100 text-bold"
          type="submit"
          form="physical-address-form"
          loading={isLoadingSubmitForm || isLoadingPartialSignup ? 'true' : 'false'}
          disabled={disabledButton}>
          {translate('confirm_button_label')}
        </button>
      </div>
    </>
  );

  const onChangeRecV2 = async (tokenV2) => {
    setRecV2Token(tokenV2);
    submitUserData({
      ...state,
      ...form,
      state_region: stateRegion,
      mailingAddress: null,
      token: tokenV2,
    });
  };

  return (
    <>
      <OnboardingBodyLayout
        withBack={!state?.hideBackBtn}
        onPrevClick={history.goBack}
        renderButtons={renderButtons}
        title={translate('address_information_title_label')}>
        <div className="alert alert-info d-flex mt-4 physical-address-alert mb-3 mb-md-4">
          <i className="icon icon-info-icon text-shadow" />
          <div className="content">{translate('physical_address_info_banner_1')}</div>
        </div>
        <form onSubmit={handleSubmitForm} id="physical-address-form" className="d-flex flex-column">
          {!isUS && (
            <div className="select-group personal-info form-group m-0">
              <SelectCountryDropdown
                name="selected_country"
                placeholder={translate('country_select_label')}
                getOptionLabel={(option) => option.displayName}
                isOptionSelected={(option) => option.iso3166alpha3 === form.selected_country_alpha}
                value={form.selected_country_label}
                flagValue={form.selected_country_alpha2}
                onChange={(data, name) => handleChange(data.iso3166alpha3, name)}
                handleBlur={handleFormBlur}
                clearError={clearFormError}
                optionName="displayName"
                prefix=""
                fieldsInfo={selectDropDownFields}
                dropDownParentClass=""
                withValueImageFlag
                readOnly
              />
            </div>
          )}
          {shouldShowStateRegion && (
            <div className="select-group personal-info form-group m-0">
              <SelectStateRegionDropdown
                name="state_region"
                placeholder={translate('state_region_select_label')}
                value={form.state_region}
                options={subdivisions}
                isLoading={isLoadingSubdivisions}
                onChange={(code, name) => handleChange(code, name)}
                handleBlur={handleFormBlur}
                clearError={clearFormError}
                fieldsInfo={stateRegionDropdownFields}
              />
            </div>
          )}
          {isUS && (
            <>
              <div className="form-check form-switch d-flex p-0 align-items-center mt-4 mb-0">
                <div className="d-flex flex-column">
                  <b className="text-f5 text-dark">{translate('mailing_address_label')}</b>
                  <p className="text-f6 text-shadow mt-1">
                    {translate('same_as_physical_address_label')}
                  </p>
                </div>
                <input
                  name="mailingAddress"
                  className="form-check-input d-flex ms-auto m-0"
                  type="checkbox"
                  onChange={handleChange}
                  checked={form.mailingAddress}
                />
              </div>
              <div className="d-flex flex-column flex-md-row w-100 flex-fill gap-5 gap-md-4">
                <div className="select-group personal-info form-group w-100 m-0">
                  <SelectCountryDropdown
                    name="selected_country"
                    placeholder={translate('country_select_label')}
                    getOptionLabel={(option) => option.displayName}
                    isOptionSelected={(option) =>
                      option.iso3166alpha3 === form.selected_country_alpha
                    }
                    value={form.selected_country_label}
                    flagValue={form.selected_country_alpha2}
                    onChange={(data, name) => handleChange(data, name)}
                    handleBlur={handleFormBlur}
                    clearError={clearFormError}
                    optionName="displayName"
                    prefix=""
                    fieldsInfo={selectDropDownFields}
                    dropDownParentClass=""
                    withValueImageFlag
                    readOnly
                  />
                </div>
                <div className="select-group personal-info form-group w-100 m-0">
                  <SelectUSStateDropdown
                    name="us_state"
                    placeholder={translate('us_state_select_label')}
                    getOptionLabel={(option) => option.postal_abbreviation}
                    isOptionSelected={(option) => option.postal_abbreviation === form.us_state}
                    value={form.us_state}
                    onChange={(data, name) => handleChange(data.postal_abbreviation, name)}
                    handleBlur={handleFormBlur}
                    clearError={clearFormError}
                    optionName="postal_abbreviation"
                    prefix=""
                    fieldsInfo={stateDropdownFields}
                    dropDownParentClass=""
                    withValueImageFlag
                  />
                </div>
              </div>
            </>
          )}
          <div className="d-flex flex-row w-100 flex-fill gap-4">
            <Input
              containerClass="w-100"
              className={`${isIOS ? 'ios' : ''}`}
              groupClassName={`m-0 ${isIOS ? 'ios' : ''}`}
              placeholder={translate('city_input_label')}
              label={translate('city_input_label')}
              errorLabel={
                !validateInputLength(form?.cityOrTown, 24, false)
                  ? translate('input_invalid_length_label', { number: 24 })
                  : translate('input_required_error_label')
              }
              name="cityOrTown"
              value={form.cityOrTown}
              onChange={handleChange}
              onBlur={handleBlur}
              onFocus={clearFormError}
              withFloatingLabel
              withErrorLabel
              required
            />
            <Input
              containerClass="w-100"
              className={`${isIOS ? 'ios' : ''}`}
              groupClassName={`m-0 ${isIOS ? 'ios' : ''}`}
              placeholder={translate('index_input_label')}
              label={translate('index_input_label')}
              errorLabel={
                form.postcode
                  ? translate('post_code_invalid_error_label')
                  : translate('input_required_error_label')
              }
              name="postcode"
              value={form.postcode}
              onChange={handleChange}
              onBlur={handleBlur}
              onFocus={clearFormError}
              withFloatingLabel
              withErrorLabel
              required
            />
          </div>
          <Input
            containerClass="w-100"
            className={`${isIOS ? 'ios' : ''}`}
            groupClassName={`m-0 ${isIOS ? 'ios' : ''}`}
            placeholder={translate('street_address_input_label')}
            label={translate('address_line_1_label_street_address')}
            errorLabel={
              !validateInputLength(form?.addressLine1, 24, false)
                ? translate('input_invalid_length_label', { number: 24 })
                : translate('input_required_error_label')
            }
            name="addressLine1"
            value={form.addressLine1}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={clearFormError}
            withFloatingLabel
            withErrorLabel
            required
          />
          <Input
            containerClass="w-100"
            className={`${isIOS ? 'ios' : ''}`}
            groupClassName={`m-0 ${isIOS ? 'ios' : ''} addressLine2`}
            placeholder={translate('house_apartment_input_label')}
            label={translate('address_line_2_label_house_number')}
            errorLabel={
              !validateInputLength(form?.addressLine2, 24, false)
                ? translate('input_invalid_length_label', { number: 24 })
                : translate('input_required_error_label')
            }
            name="addressLine2"
            value={form.addressLine2}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={clearFormError}
            withFloatingLabel
            withErrorLabel
          />
        </form>
      </OnboardingBodyLayout>
      <RecaptchaModal
        onChange={onChangeRecV2}
        isLoading={isLoadingSubmitForm}
        resetToken={() => setRecV2Token('')}
        token={recV2Token}
        setIsModalVisible={setIsOpenRecV2}
        isModalVisible={isOpenRecV2}
      />
    </>
  );
};

export default AddPhysicalAddress;
